// Component to be rendered in the settings page  and is related to change email and password

import { Button, notification, Menu, Popconfirm, Spin } from "antd";
import { useRef, useState } from "react";
import { getSupaClient } from "../../lib/supabase";
import { useForm } from "react-hook-form";
import { FormLabel } from "../TriggerEditor/FormLabel";
import { Input } from "../TriggerEditor/Input";
import useUserSettings from "./useUserSettings";
export default function UserSettings() {
  const supabase = getSupaClient();

  const currentUserEmail = supabase.auth.session()!.user!.email;
  const [currentTab, setCurrentTab] = useState("email");
  const { changeEmail, changePassword } = useUserSettings();

  const emailChangeForm = useForm({
    defaultValues: {
      email: currentUserEmail,
    },
    mode: "onChange",
  });

  const passwordChangeForm = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const password = useRef({});
  password.current = passwordChangeForm.watch("password", "");

  const onUpdateEmail = function (formData: any) {
    console.log("formData", formData);
    if (formData.email === currentUserEmail) {
      notification.info({
        message: "Email is the same!",
      });
      return;
    }
    changeEmail.mutate(formData, {
      onSuccess: () => {
        window.analytics.track(
          `Email changed from ${currentUserEmail} to ${formData.email}`
        );
        notification.success({
          message: "Email updated!",
        });
        setTimeout(() => {
          supabase.auth.signOut();
          window.location.reload();
        }, 500);
      },
      onError: (err: any) => {
        console.log("Error updating email");

        if (err.reason === "Email already exists") {
          notification.error({
            message: "Email already exists! Please try with a different email",
          });
        } else {
          window.analytics.track(
            `Unknown error changing email from ${currentUserEmail} to ${
              formData.email
            }, error: ${JSON.stringify(err)}`
          );
          notification.error({
            message: "Error updating email! Please try again later",
          });
        }
      },
    });
  };

  const onUpdatePassword = function (formData: any) {
    changePassword.mutate(formData, {
      onSuccess: () => {
        window.analytics.track(
          `User ${currentUserEmail}  changed its password`
        );
        notification.success({
          message: "Password updated!",
        });
      },
      onError: (err: any) => {
        console.log("Error updating password ", JSON.stringify(err));
        window.analytics.track(
          `Unknown error changing password, error: ${JSON.stringify(err)}`
        );
        notification.error({
          message: "Error updating password! Please try again later",
        });
      },
    });
  };

  const changeEmailComponent = (
    <form className="flex flex-col gap-3 text-left">
      <FormLabel label="Email">
        <Input
          type={"text"}
          disabled={changeEmail.isLoading}
          style={{ width: "100%" }}
          {...emailChangeForm.register("email", {
            required: true,
            // This regex is taken from https://www.emailregex.com/. It's the regex used in type="email" from W3C.
            pattern:
              /([a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+)/,
          })}
        />
        {emailChangeForm.formState.errors.email && (
          <p className="text-red-500 m-0">Invalid email!</p>
        )}
      </FormLabel>
      <div>
        {" "}
        <Popconfirm
          title="After changing your email, you will be logged out. Are you sure you want to continue?"
          onConfirm={emailChangeForm.handleSubmit(onUpdateEmail)}
          okText="Yes"
          cancelText="No"
          disabled={!emailChangeForm.formState.isValid || changeEmail.isLoading}
        >
          <Button
            type="primary"
            disabled={
              !emailChangeForm.formState.isValid || changeEmail.isLoading
            }
            style={{ width: "100%" }}
          >
            <div className="flex gap-4 justify-center w-full">
              {changeEmail.isLoading && <Spin />}
              <span>Update email</span>
            </div>
          </Button>
        </Popconfirm>
      </div>
    </form>
  );

  const changePasswordComponent = (
    <form
      className="flex flex-col gap-3 text-left"
      onSubmit={passwordChangeForm.handleSubmit(onUpdatePassword)}
    >
      <FormLabel label="Password">
        <Input
          type={"password"}
          disabled={changePassword.isLoading}
          style={{ width: "100%" }}
          {...passwordChangeForm.register("password", {
            required: "Password is required",
            minLength: {
              value: 6,
              message: "Password must be at least 6 characters long",
            },
          })}
        />
        {passwordChangeForm.formState.errors.password && (
          <p className="text-red-500 m-0">
            {passwordChangeForm.formState.errors.password.message}
          </p>
        )}
      </FormLabel>
      <FormLabel label="Confirm password">
        <Input
          type={"password"}
          disabled={changePassword.isLoading}
          style={{ width: "100%" }}
          {...passwordChangeForm.register("confirmPassword", {
            required: "Confirm password is required",
            validate: (value) =>
              value === password.current || "Passwords do not match",
          })}
        />
        {passwordChangeForm.formState.errors.confirmPassword && (
          <p className="text-red-500 m-0">
            {passwordChangeForm.formState.errors.confirmPassword.message}
          </p>
        )}
      </FormLabel>
      <div>
        {" "}
        <Button
          type="primary"
          htmlType="submit"
          disabled={
            !passwordChangeForm.formState.isValid || changePassword.isLoading
          }
          style={{ width: "100%" }}
        >
          <div className="flex gap-4 justify-center w-full">
            {changePassword.isLoading && <Spin />}
            <span>Update password</span>
          </div>
        </Button>
      </div>
    </form>
  );

  return (
    <div className="user-settings-box">
      <Menu
        mode="horizontal"
        defaultSelectedKeys={[currentTab]}
        onClick={(e) => setCurrentTab(e.key)}
      >
        <Menu.Item key="email">Change Email</Menu.Item>
        <Menu.Item key="password">Change Password</Menu.Item>
      </Menu>

      {currentTab === "email" && changeEmailComponent}
      {currentTab === "password" && changePasswordComponent}
    </div>
  );
}
